import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL, getAuthHeader } from '../../../services/api';

// Fetch all bookings
export const fetchAdminBookings = createAsyncThunk('admin/bookings', async (_, thunkAPI) => {
  const res = await axios.get(`${API_BASE_URL}/admin/bookings`, { headers: getAuthHeader() });
  return res.data;
});

// Update booking status
export const updateBookingStatusAdmin = createAsyncThunk('admin/updateBookingStatus', async ({ id, status }, thunkAPI) => {
  const res = await axios.put(`${API_BASE_URL}/admin/bookings/${id}/status`, { status }, { headers: getAuthHeader() });
  return { id, status };
});

// Fetch users
export const fetchAdminUsers = createAsyncThunk('admin/users', async () => {
  const res = await axios.get(`${API_BASE_URL}/admin/users`, { headers: getAuthHeader() });
  return res.data;
});

// Update user role
export const updateUserRoleAdmin = createAsyncThunk('admin/updateUserRole', async ({ id, isAdmin }) => {
  const res = await axios.put(`${API_BASE_URL}/admin/users/${id}/role`, { isAdmin }, { headers: getAuthHeader() });
  return res.data;
});

// The backend's validation message is passed through so admin forms can show
// what's wrong.
const apiError = (error) =>
  error.response?.data?.errors?.[0]?.message || error.response?.data?.message || error.message;

// Fetch rooms
export const fetchAdminRooms = createAsyncThunk('admin/rooms', async () => {
  const res = await axios.get(`${API_BASE_URL}/admin/rooms`, { headers: getAuthHeader() });
  return res.data;
});

// Create room - roomData is a FormData (it may include photos)
export const createRoomAdmin = createAsyncThunk('admin/createRoom', async (roomData, thunkAPI) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/admin/rooms`, roomData, { headers: getAuthHeader() });
    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiError(error));
  }
});

// Update room - roomData is a FormData; keepImages lists the photos to keep
export const updateRoomAdmin = createAsyncThunk('admin/updateRoom', async ({ id, roomData }, thunkAPI) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/admin/rooms/${id}`, roomData, { headers: getAuthHeader() });
    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiError(error));
  }
});

// Delete room
export const deleteRoomAdmin = createAsyncThunk('admin/deleteRoom', async (id) => {
  await axios.delete(`${API_BASE_URL}/admin/rooms/${id}`, { headers: getAuthHeader() });
  return id;
});

// Fetch dogs
export const fetchAdminDogs = createAsyncThunk('admin/dogs', async () => {
  const res = await axios.get(`${API_BASE_URL}/admin/dogs`, { headers: getAuthHeader() });
  return res.data;
});

// Delete dog
export const deleteDogAdmin = createAsyncThunk('admin/deleteDog', async (id) => {
  await axios.delete(`${API_BASE_URL}/admin/dogs/${id}`, { headers: getAuthHeader() });
  return id;
});

// Products (add-ons for bookings)

export const fetchAdminProducts = createAsyncThunk('admin/products', async () => {
  const res = await axios.get(`${API_BASE_URL}/products/admin/all`, { headers: getAuthHeader() });
  return res.data.products;
});

// productData is a FormData (it may include an image file)
export const createProductAdmin = createAsyncThunk('admin/createProduct', async (productData, thunkAPI) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/products`, productData, { headers: getAuthHeader() });
    return res.data.product;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiError(error));
  }
});

export const updateProductAdmin = createAsyncThunk('admin/updateProduct', async ({ id, productData }, thunkAPI) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/products/${id}/update`, productData, { headers: getAuthHeader() });
    return res.data.product;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiError(error));
  }
});

export const deleteProductAdmin = createAsyncThunk('admin/deleteProduct', async (id) => {
  await axios.delete(`${API_BASE_URL}/products/${id}/delete`, { headers: getAuthHeader() });
  return id;
});

// Store orders (only paid ones come back from the API)
export const fetchAdminOrders = createAsyncThunk('admin/orders', async () => {
  const res = await axios.get(`${API_BASE_URL}/orders/admin/all`, { headers: getAuthHeader() });
  return res.data.orders;
});

export const updateOrderStatusAdmin = createAsyncThunk('admin/updateOrderStatus', async ({ id, status }, thunkAPI) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/orders/admin/${id}/status`, { status }, { headers: getAuthHeader() });
    return res.data.order;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiError(error));
  }
});

// Fetch stats
export const fetchAdminStats = createAsyncThunk('admin/stats', async () => {
  const res = await axios.get(`${API_BASE_URL}/admin/stats`, { headers: getAuthHeader() });
  return res.data;
});

const initialState = {
  bookings: [],
  users: [],
  rooms: [],
  dogs: [],
  products: [],
  orders: [],
  stats: null,
  loading: false,
  error: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminBookings.fulfilled, (state, action) => { state.bookings = action.payload; })
      .addCase(updateBookingStatusAdmin.fulfilled, (state, action) => {
        const index = state.bookings.findIndex(b => b._id === action.payload.id);
        if (index !== -1) state.bookings[index].status = action.payload.status;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => { state.users = action.payload; })
      .addCase(updateUserRoleAdmin.fulfilled, (state, action) => {
        const index = state.users.findIndex(u => u._id === action.payload._id);
        if (index !== -1) state.users[index] = action.payload;
      })
      .addCase(fetchAdminRooms.fulfilled, (state, action) => { state.rooms = action.payload; })
      .addCase(createRoomAdmin.fulfilled, (state, action) => { state.rooms.push(action.payload); })
      .addCase(updateRoomAdmin.fulfilled, (state, action) => {
        const index = state.rooms.findIndex(r => r._id === action.payload._id);
        if (index !== -1) state.rooms[index] = action.payload;
      })
      .addCase(deleteRoomAdmin.fulfilled, (state, action) => {
        state.rooms = state.rooms.filter(r => r._id !== action.payload);
      })
      .addCase(fetchAdminDogs.fulfilled, (state, action) => { state.dogs = action.payload; })
      .addCase(deleteDogAdmin.fulfilled, (state, action) => {
        state.dogs = state.dogs.filter(d => d._id !== action.payload);
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => { state.products = action.payload; })
      .addCase(createProductAdmin.fulfilled, (state, action) => { state.products.unshift(action.payload); })
      .addCase(updateProductAdmin.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p._id === action.payload._id);
        if (index !== -1) state.products[index] = action.payload;
      })
      .addCase(deleteProductAdmin.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p._id !== action.payload);
      })
      .addCase(fetchAdminOrders.fulfilled, (state, action) => { state.orders = action.payload; })
      .addCase(updateOrderStatusAdmin.fulfilled, (state, action) => {
        const index = state.orders.findIndex(o => o._id === action.payload._id);
        if (index !== -1) state.orders[index] = action.payload;
      })
      .addCase(fetchAdminStats.fulfilled, (state, action) => { state.stats = action.payload; });
  },
});

export default adminSlice.reducer;